<template>
  <div class="login-container">
    <div class="login-box">
      <div class="login-header">
        <h1>唯电能源</h1>
        <p>数据可视化平台</p>
      </div>
      <el-form 
        ref="loginForm" 
        :model="loginForm" 
        :rules="rules"
        class="login-form"
      >
        <el-form-item prop="username">
          <el-input
            v-model="loginForm.username"
            placeholder="请输入用户名"
            @keyup.enter.native="handleLogin"
          >
            <template #prefix>
              <i class="el-icon-user"></i>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item prop="password">
          <el-input
            v-model="loginForm.password"
            type="password"
            placeholder="请输入密码"
            @keyup.enter.native="handleLogin"
            show-password
          >
            <template #prefix>
              <i class="el-icon-lock"></i>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            class="login-btn"
            :loading="loading"
            @click="handleLogin"
          >
            {{ loading ? '登录中...' : '登录' }}
          </el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 加载进度条 -->
    <el-dialog
      v-model="showLoading"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      width="300px"
      custom-class="loading-dialog"
    >
      <div class="loading-content">
        <i class="el-icon-loading"></i>
        <p>正在进入全屏模式...</p>
        <el-progress 
          :percentage="loadingProgress" 
          :show-text="false"
          :stroke-width="4"
          :color="customColors"
        ></el-progress>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { login } from '@/apis/index.js'

export default {
  name: 'LoginView',
  data() {
    return {
      loginForm: {
        username: '',
        password: ''
      },
      loading: false,
      showLoading: false,
      loadingProgress: 0,
      customColors: [
        { color: '#00f2fe', percentage: 50 },
        { color: '#4facfe', percentage: 100 }
      ],
      rules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { min: 3, max: 20, message: '长度在 3 到 20 个字符', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 5, max: 20, message: '长度在 6 到 20 个字符', trigger: 'blur' }
        ]
      }
    }
  },
  methods: {
    async handleLogin() {
      this.$refs.loginForm.validate(async valid => {
        if (valid) {
          this.loading = true
          
          try {
            // 调用登录接口
            const loginData = {
              userAccount: this.loginForm.username,
              userPassword: this.loginForm.password
            }
            
            const response = await login(loginData)
            
            // 检查登录结果
            if (response.success === true) {
              // 登录成功，显示加载进度条
              this.showLoading = true
              
              try {
                // 请求全屏
                await document.documentElement.requestFullscreen()
                
                // 模拟进度条
                const progressInterval = setInterval(() => {
                  if (this.loadingProgress < 100) {
                    this.loadingProgress += 10
                  } else {
                    clearInterval(progressInterval)
                    // 完成加载后关闭对话框
                    setTimeout(() => {
                      this.showLoading = false
                      // 设置认证状态
                      localStorage.setItem('isAuthenticated', 'true')
                      this.$router.push('/')
                    }, 500)
                  }
                }, 200)
              } catch (error) {
                this.$message({
                  type: 'warning',
                  message: '全屏模式启动失败，请手动点击全屏按钮'
                })
                this.showLoading = false
                this.loading = false
              }
            } else {
              // 登录失败
              this.$message({
                type: 'error',
                message: response.message || '登录失败，请检查用户名和密码'
              })
              this.loading = false
            }
          } catch (error) {
            // 网络错误或其他异常
            console.error('登录请求失败:', error)
            this.$message({
              type: 'error',
              message: error
            })
            this.loading = false
          }
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
.login-container {
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: url('@/assets/imgs/bg.png') no-repeat center center;
  background-size: cover;
  position: relative;
  overflow: hidden;
 
}

.login-box {
  position: relative;
  width: 420px;
  padding: 50px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  z-index: 1;
  transform: translateY(0);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }
}

.login-header {
  text-align: center;
  margin-bottom: 50px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, transparent, #4facfe, transparent);
    border-radius: 3px;
  }

  h1 {
    font-size: 36px;
    color: #fff;
    margin: 0;
    font-weight: 700;
    background: linear-gradient(120deg, #00f2fe, #4facfe);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    text-shadow: 0 2px 15px rgba(79, 172, 254, 0.3);
    letter-spacing: 2px;
  }

  p {
    font-size: 18px;
    color: rgba(255, 255, 255, 0.8);
    margin: 12px 0 0;
    letter-spacing: 3px;
    font-weight: 300;
  }
}

.login-form {
  :deep(.el-form-item) {
    margin-bottom: 30px;
  }

  :deep(.el-input) {
    .el-input__inner {
      height: 52px;
      line-height: 52px;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 12px;
      color: #fff;

      &::placeholder {
        color: rgba(255, 255, 255, 0.4);
      }

      &:focus {
        border-color: #4facfe;
        background: rgba(255, 255, 255, 0.08);
      }
    }

    .el-input__prefix {
      color: rgba(255, 255, 255, 0.6);
    }
  }

  :deep(.el-form-item__error) {
    color: #ff4d4f;
  }
}

.login-btn {
  width: 100%;
  height: 52px;
  background: linear-gradient(120deg, #00f2fe, #4facfe);
  border: none;
  border-radius: 12px;
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 2px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(79, 172, 254, 0.4);
  }
}

@media screen and (max-width: 480px) {
  .login-box {
    width: 90%;
    padding: 40px 25px;
    margin: 20px;
  }

  .login-header {
    h1 {
      font-size: 32px;
    }

    p {
      font-size: 16px;
    }
  }

  .login-form {
    :deep(.el-input) {
      .el-input__inner {
        height: 48px;
        line-height: 48px;
      }
    }
  }

  .login-btn {
    height: 48px;
  }
}

:deep(.loading-dialog) {
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 30px;
  }
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;

  i {
    font-size: 40px;
    color: #4facfe;
  }

  p {
    color: #fff;
    font-size: 16px;
    margin: 0;
  }

  .el-progress {
    width: 100%;
  }
}
</style> 